import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { type SystemSettingHistoryEntry, listSettingHistory } from '@/lib/api/settings';

interface Props {
  settingKey: string | null;
  label: string;
  onClose: () => void;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function HistoryRow({ entry }: { entry: SystemSettingHistoryEntry }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <Stack spacing={0.25} sx={{ minWidth: 130 }}>
        <Typography variant="caption" color="text.secondary">
          {formatTime(entry.createdAt)}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            {entry.staff.nickname}
          </Typography>
          <Chip
            size="small"
            label={entry.staff.role}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.7rem' }}
          />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.disabled">
          {entry.beforeValue ?? '—'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          →
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {entry.afterValue ?? '—'}
        </Typography>
      </Stack>
    </Stack>
  );
}

export function SettingHistoryDialog({ settingKey, label, onClose }: Props) {
  const open = settingKey !== null;
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'history', settingKey],
    queryFn: () => listSettingHistory(settingKey!),
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.25}>
            <Typography variant="subtitle1" fontWeight={700}>
              변경 이력
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            불러오는 중…
          </Typography>
        ) : !data || data.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 2 }}>
            변경 이력이 아직 없습니다.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {data.map((e) => (
              <HistoryRow key={e.id} entry={e} />
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
